import React, { FC, useState } from 'react'
import {
  ButtonGroup,
  FlexContainer,
  Text2XL,
  TextBase,
  elFlexAlignCenter,
  elFlexJustifyCenter,
  elMb1,
  elMb5,
  elPy5,
  elWFull,
  useMediaQuery,
} from '@reapit/elements'
import { ErrorBoundary } from '@reapitform/utils'
import { css, cx } from '@linaria/core'
import { useParams } from 'react-router-dom'
import StepNavigation from '../view/step-navigation'
import { PageWrapper } from '@reapitform/common-ui'
import { useQuery } from '@tanstack/react-query'
import { formStepQuery } from '../api/step/step-loader'
import { RenderFormCard } from '../card/render-form-card'
import { DialogChangedRequest } from '../dialog-canged-request'
import { DialogHelp } from '../dialog-help-agreement'

export const Steps: FC = () => {
  const [isChangeRequestOpen, setIsChangeRequestOpen] = useState<boolean>(false)
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false)

  const { recipientId = '', stepsId = '' } = useParams<string>()
  const { isMobile } = useMediaQuery()
  const { data: formStepData } = useQuery(formStepQuery(recipientId, stepsId))

  const pointerCss = css`
    cursor: pointer;
  `

  function navigateToHelpPage(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    event.preventDefault()
    setIsHelpOpen(true)
  }

  function showRequestAChangeForm(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    event.preventDefault()
    setIsChangeRequestOpen(true)
  }

  return (
    <ErrorBoundary>
      <StepNavigation
        steps={{
          nextStep: formStepData?.nextStep,
          lastStep: formStepData?.stepDisplayTotal,
          previousStep: formStepData?.previousStep,
          currentStep: formStepData?.stepDisplayNumber,
        }}
        nextButtonText={formStepData?.continueButtonText || 'Continue'}
        hasDisabledNextButton={!formStepData?.continueButtonEnabled}
      >
        <PageWrapper key={formStepData?.stepDisplayName} hasGreyBackground>
          <FlexContainer isFlexColumn className={cx(elPy5, elFlexAlignCenter, elWFull)}>
            <FlexContainer
              isFlexColumn
              className={cx(
                elWFull,
                css`
                  max-width: 736px;
                  width: 100%;
                `,
              )}
            >
              <TextBase intent="low" className={elMb1}>
                Step {formStepData?.stepDisplayNumber} of {formStepData?.stepDisplayTotal}
              </TextBase>
              <Text2XL hasBoldText className={elMb5}>
                {formStepData?.stepDisplayName}
              </Text2XL>
              <TextBase hasSectionMargin>{formStepData?.stepDescription}</TextBase>
              <FlexContainer
                isFlexColumn
                className={css`
                  gap: 0.75rem;
                `}
              >
                {Array(formStepData?.totalCards || 0)
                  .fill(null)
                  .map((_, index) => (
                    <RenderFormCard
                      key={`${recipientId}-${stepsId}-${index + 1}`}
                      cardId={`${index + 1}`}
                      recipientId={recipientId}
                      stepId={stepsId}
                    />
                  ))}
              </FlexContainer>

              <ButtonGroup
                className={cx(
                  css`
                    margin-top: var(--util-rems-9);
                  `,
                  elFlexJustifyCenter,
                )}
              >
                {!isMobile && formStepData?.showHelp && (
                  <a href="#" onClick={navigateToHelpPage}>
                    <TextBase hasMediumText intent="primary" className={pointerCss}>
                      Get Help
                    </TextBase>
                  </a>
                )}
                {formStepData?.showRequestChange && (
                  <a href="#" onClick={showRequestAChangeForm}>
                    <TextBase hasMediumText intent="primary" className={pointerCss}>
                      Request a Change
                    </TextBase>
                  </a>
                )}
              </ButtonGroup>
            </FlexContainer>
          </FlexContainer>
        </PageWrapper>
      </StepNavigation>
      <DialogChangedRequest isModalOpen={isChangeRequestOpen} handlerModalClose={() => setIsChangeRequestOpen(false)} />
      {!isMobile && formStepData?.showHelp && (
        <DialogHelp isModalOpen={isHelpOpen} handlerModalClose={() => setIsHelpOpen(false)} />
      )}
    </ErrorBoundary>
  )
}
